// useProfileSettingHelper.tsx
import { useState, useEffect, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import { userProfile } from "../../interfaces/user/detail";
import { GetTokenFromIndexedDB } from "../../utils/indexedDBUtils/get-indexed-db";
import { DeleteTokenInIndexDB } from "../../utils/indexedDBUtils/delete-index-db";
import IsTokenExpired from "../../utils/isTokenExpiredFunction/is-token-expired";
import { toast } from "react-toastify";
import { compressImageToDataURL } from "../../utils/image/compressImage";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { deleteProfilePhoto, deleteUserAccount, getProfilePhoto, getUserProfile, updateUserProfile, uploadProfilePhoto } from "../../data-access/apis/common/profile";
import { useNavigate } from "react-router-dom";

export interface ProfileErrors {
  field: string;
  message: string;
}

const ACCEPTED_MIME = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_FILE_SIZE_MB = 5; // before compression

export const useProfileSettingHelper = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const [isUserDetails, setIsUserDetails] = useState({} as userProfile);

  const userProfilePhoto = useSelector((state: RootState) => state.user);
  const getUserPhoto = useSelector((state: RootState) => state.user);
  const loadProfilePhoto = userProfilePhoto.user?.profileImage;

  // 🔥 Component mount hone ke sath hi profile photo fetch ho jayegi
  useEffect(() => {
    dispatch(getProfilePhoto());
    dispatch(getUserProfile());
  }, [dispatch]);
  // 🔹 Jab bhi userUserPhoto update ho
  useEffect(() => {
    if (getUserPhoto?.user) {
      setIsUserDetails(getUserPhoto.user as userProfile);
    }
  }, [getUserPhoto]);


  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [isDirty, setIsDirty] = useState(false);
  const [formErrors, setFormErrors] = useState<ProfileErrors[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // --- PHOTO state ---
  const [photoPreview, setPhotoPreview] = useState<string | null>(null); // UI preview
  const [photoCompressedDataUrl, setPhotoCompressedDataUrl] = useState<string | null>(null); // payload only if changed
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [photoDirty, setPhotoDirty] = useState(false); // changed?
  const [photoRemoved, setPhotoRemoved] = useState(false); // delete flag

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ✅ Load user details from token
  useEffect(() => {
    const checkToken = async () => {
      const token = await GetTokenFromIndexedDB("authToken");
      if (token) {
        const decodedToken = jwtDecode<userProfile>(token);
        const isTokenValid = !IsTokenExpired(decodedToken);

        // setIsUserDetails(decodedToken);

        if (!isTokenValid) {
          await DeleteTokenInIndexDB("authToken");
        }
      }
    };

    checkToken();
  }, []);

  useEffect(() => {
    if (isUserDetails) {
      setFullName(isUserDetails?.name || "");
      setUsername(isUserDetails?.username || "");
      setEmail(isUserDetails?.email || "");
      setPhone(String(isUserDetails?.phone || ""));
      setPhotoCompressedDataUrl(null);
      setPhotoDirty(false);
      setPhotoRemoved(false);
    }
  }, [isUserDetails]);

  // ✅ Validation
  const validateForm = () => {
    const errors: ProfileErrors[] = [];

    if (!fullName.trim()) {
      errors.push({ field: "fullName", message: "Full name is required." });
    }

    if (!username.trim()) {
      errors.push({ field: "username", message: "Username is required." });
    }

    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      errors.push({ field: "email", message: "Valid email is required." });
    }

    if (!String(phone).trim() || !/^\d{10}$/.test(String(phone))) {
      errors.push({ field: "phone", message: "Phone must be 10 digits." });
    }

    if (isEditingPassword) {
      if (!oldPassword.trim()) {
        errors.push({ field: "oldPassword", message: "Old password is required." });
      }
      if (!newPassword.trim() || newPassword.length < 6) {
        errors.push({
          field: "newPassword",
          message: "New password must be at least 6 characters.",
        });
      }
    }
    if (photoError) {
      errors.push({ field: "profileImage", message: photoError });
    }

    setFormErrors(errors);
    return errors.length === 0;
  };

  // --- Photo handlers ---
  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = async (file: File | null) => {
    setPhotoError(null);
    if (!file) return;

    if (!ACCEPTED_MIME.includes(file.type)) {
      const msg = "Only JPG, PNG or WEBP allowed.";
      setPhotoError(msg);
      toast.error(msg, { position: "bottom-right" });
      return;
    }

    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_FILE_SIZE_MB) {
      const msg = `Max file size ${MAX_FILE_SIZE_MB} MB.`;
      setPhotoError(msg);
      toast.error(msg, { position: "bottom-right" });
      return;
    }

    try {
      const dataUrl = await compressImageToDataURL(file, {
        maxWidth: 512,
        maxHeight: 512,
        quality: 0.82,
        outputType: "image/jpeg",
      });

      setPhotoPreview(dataUrl);

      // ✅ Dispatch API call (sirf file)
      dispatch(uploadProfilePhoto(file))

    } catch (err) {
      console.error(err);
      const msg = "Failed to process image.";
      setPhotoError(msg);
      toast.error(msg, { position: "bottom-right" });
    }
  };

  const handleDeletePhoto = () => {
    setPhotoPreview(null);
    setPhotoCompressedDataUrl(null);
    setPhotoRemoved(true);
    setPhotoDirty(true);
    setIsDirty(true);
    dispatch(deleteProfilePhoto())
  };

  // ✅ Handle Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      formErrors.forEach((err) =>
        toast.error(err.message, {
          position: "bottom-right",
          autoClose: 3000,
        })
      );
      return;
    }

    // ✅ Always send profile details
    const payload: any = {
      name: fullName,
      username: username,
      email: email,
      phone: phone,
    };

    if (isEditingPassword) {
      payload.oldPassword = oldPassword;
      payload.newPassword = newPassword;
    }

    // ✅ Only send profile photo if user uploaded/changed one
    if (isUserDetails?.profileImage !== undefined) {
      payload.profileImage = isUserDetails.profileImage;
    }

    // ✅ Photo logic
    // 1) agar user ne delete click kiya
    if (photoDirty && photoRemoved) {
      payload.profileImageRemove = true; // server old image delete kare, db space bache
    }
    // 2) agar user ne new photo set ki (compressed dataURL)
    if (photoDirty && !photoRemoved && photoCompressedDataUrl) {
      payload.profileImage = photoCompressedDataUrl; // compact data, ~<200KB typical
    }
    // NOTE: agar photoDirty false hai to kuch bhi na bhejo → bandwidth/storage dono बचेंगे

    try {
      setIsProcessing(true);

      // 🔥 API Call ke liye ready payload
      dispatch(updateUserProfile(payload))

      setIsDirty(false);
      setPhotoDirty(false);
      setPhotoRemoved(false);
    } catch (error: any) {
      toast.error("Something went wrong while updating profile.", {
        position: "bottom-right",
        autoClose: 3000,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // ✅ Handle Delete Account
  const handleDeleteAccount = async () => {

    const payload = { _id: isUserDetails._id };
    const resultAction = await dispatch(deleteUserAccount(payload));

    if (deleteUserAccount.fulfilled.match(resultAction)) {
      await DeleteTokenInIndexDB('authToken');
      navigate('/')
    }
  };

  return {
    fullName,
    setFullName,
    username,
    setUsername,
    email,
    setEmail,
    phone,
    setPhone,
    oldPassword,
    setOldPassword,
    newPassword,
    setNewPassword,
    showOldPassword,
    setShowOldPassword,
    showPassword,
    setShowPassword,
    isEditingPassword,
    setIsEditingPassword,
    isDirty,
    setIsDirty,
    formErrors,
    handleSubmit,
    isUserDetails,
    isProcessing,
    handleDeleteAccount, // 👈 return करो 
    // photo
    fileInputRef,
    photoPreview,
    photoError,
    openFilePicker,
    handlePhotoChange,
    handleDeletePhoto,
    loadProfilePhoto,
  };
};
